<template>
  <div v-if="currentUser.superAdmin">
    <v-tabs v-model="activeTab" background-color="grey lighten-3" class="elevation-1">
      <v-tab>{{ $t("pages.disposableEmails.tabBlocklist") }}</v-tab>
      <v-tab>{{ $t("pages.disposableEmails.tabAllowlist") }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-data-table
          :headers="blocklistHeaders"
          :items="filteredBlocklist"
          :loading="loadingBlocklist"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [10, 20, 50, 100, -1]
          }"
          :items-per-page="20"
          :search="blocklistSearch"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-select
                v-model="blocklistSourceFilter"
                :items="sourceFilterOptions"
                :label="$t('pages.disposableEmails.source')"
                hide-details
                dense
                style="max-width: 220px"
                @change="loadBlocklist"
              ></v-select>
              <v-text-field
                v-model="blocklistSearch"
                :label="$t('pages.disposableEmails.searchDomain')"
                prepend-inner-icon="mdi-magnify"
                hide-details
                dense
                clearable
                class="ml-4"
                style="max-width: 320px"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="openAddBlocklistDialog">
                <v-icon left>mdi-plus</v-icon>
                {{ $t("pages.disposableEmails.addDomainBtn") }}
              </v-btn>
            </v-toolbar>
          </template>

          <template v-slot:item.source="{ item }">
            <v-chip :color="sourceChipColor(item.source)" small dark>{{ item.source }}</v-chip>
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:item.note="{ item }">
            <span :title="item.note">{{ truncate(item.note, 80) }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-avatar color="red darken-4" size="26" rounded>
              <v-icon dark @click="confirmDeleteBlocklist(item)">mdi-delete</v-icon>
            </v-avatar>
          </template>
        </v-data-table>
      </v-tab-item>

      <v-tab-item>
        <v-data-table
          :headers="allowlistHeaders"
          :items="allowlist"
          :loading="loadingAllowlist"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [10, 20, 50, 100, -1]
          }"
          :items-per-page="20"
          :search="allowlistSearch"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-text-field
                v-model="allowlistSearch"
                :label="$t('pages.disposableEmails.searchDomain')"
                prepend-inner-icon="mdi-magnify"
                hide-details
                dense
                clearable
                style="max-width: 320px"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="openAddAllowlistDialog">
                <v-icon left>mdi-plus</v-icon>
                {{ $t("pages.disposableEmails.addDomainBtn") }}
              </v-btn>
            </v-toolbar>
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:item.note="{ item }">
            <span :title="item.note">{{ truncate(item.note, 80) }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-avatar color="red darken-4" size="26" rounded>
              <v-icon dark @click="confirmDeleteAllowlist(item)">mdi-delete</v-icon>
            </v-avatar>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="addDialog.open" max-width="520" @keydown.esc="closeAddDialog">
      <v-card>
        <v-card-title>{{ addDialogTitle }}</v-card-title>
        <v-card-text>
          <v-form ref="addForm" v-model="addDialog.valid" lazy-validation>
            <v-text-field
              v-model="addDialog.domain"
              :label="$t('pages.disposableEmails.domain')"
              :placeholder="$t('pages.disposableEmails.domainPlaceholder')"
              :rules="domainRules"
              autofocus
              required
            ></v-text-field>
            <v-textarea
              v-model="addDialog.note"
              :label="$t('pages.disposableEmails.note')"
              :placeholder="$t('pages.disposableEmails.notePlaceholder')"
              :hint="$t('pages.disposableEmails.noteHint')"
              :rules="noteRules"
              rows="2"
              auto-grow
              counter="500"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddDialog">{{ $t("common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="addDialog.saving" @click="submitAddDialog">
            {{ $t("common.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog ref="confirm" />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          {{ $t("common.close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import UserApiService from "@/core/services/api.service.user";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ConfirmDialog from "@/view/pages/ConfirmDialog";
import { mapGetters } from "vuex";
import moment from "moment";

const SOURCE_VALUES = ["MANUAL", "GITHUB", "BUNDLED"];

export default {
  name: "disposable-emails",
  components: { ConfirmDialog },
  computed: {
    ...mapGetters(["currentUser"]),
    blocklistHeaders() {
      return [
        { text: "ID", value: "id", width: "80" },
        { text: this.$i18n.t("pages.disposableEmails.domain"), value: "domain" },
        { text: this.$i18n.t("pages.disposableEmails.source"), value: "source", width: "120" },
        { text: this.$i18n.t("pages.disposableEmails.note"), value: "note" },
        {
          text: this.$i18n.t("pages.disposableEmails.createdAt"),
          value: "createdAt",
          width: "180"
        },
        {
          text: this.$i18n.t("pages.disposableEmails.createdBy"),
          value: "createdBy",
          width: "140"
        },
        { text: "", value: "actions", sortable: false, width: "80", align: "center" }
      ];
    },
    allowlistHeaders() {
      return [
        { text: "ID", value: "id", width: "80" },
        { text: this.$i18n.t("pages.disposableEmails.domain"), value: "domain" },
        { text: this.$i18n.t("pages.disposableEmails.note"), value: "note" },
        {
          text: this.$i18n.t("pages.disposableEmails.createdAt"),
          value: "createdAt",
          width: "180"
        },
        {
          text: this.$i18n.t("pages.disposableEmails.createdBy"),
          value: "createdBy",
          width: "140"
        },
        { text: "", value: "actions", sortable: false, width: "80", align: "center" }
      ];
    },
    sourceFilterOptions() {
      return [
        { text: this.$i18n.t("pages.disposableEmails.sourceAll"), value: null },
        ...SOURCE_VALUES.map(v => ({ text: v, value: v }))
      ];
    },
    filteredBlocklist() {
      return this.blocklist;
    },
    addDialogTitle() {
      return this.addDialog.target === "allowlist"
        ? this.$i18n.t("pages.disposableEmails.addToAllowlistTitle")
        : this.$i18n.t("pages.disposableEmails.addToBlocklistTitle");
    },
    domainRules() {
      return [v => !!(v && v.trim()) || this.$i18n.t("pages.disposableEmails.domainRequired")];
    },
    noteRules() {
      return [v => !v || v.length <= 500 || this.$i18n.t("pages.disposableEmails.noteTooLong")];
    }
  },
  data() {
    return {
      activeTab: 0,
      blocklist: [],
      allowlist: [],
      loadingBlocklist: false,
      loadingAllowlist: false,
      blocklistSourceFilter: "MANUAL",
      blocklistSearch: "",
      allowlistSearch: "",
      addDialog: {
        open: false,
        target: "blocklist",
        domain: "",
        note: "",
        valid: true,
        saving: false
      },
      snackbar: { show: false, color: "success", text: "" }
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.$i18n.t("pages.disposableEmails.title") }]);
    this.loadBlocklist();
    this.loadAllowlist();
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return moment
        .utc(date)
        .local()
        .format("YYYY-MM-DD HH:mm:ss");
    },
    truncate(text, n) {
      if (!text) return "";
      return text.length > n ? text.slice(0, n) + "…" : text;
    },
    sourceChipColor(source) {
      if (source === "MANUAL") return "indigo";
      if (source === "GITHUB") return "grey darken-2";
      if (source === "BUNDLED") return "blue-grey";
      return "grey";
    },
    showSnackbar(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    async loadBlocklist() {
      this.loadingBlocklist = true;
      try {
        const resp = await UserApiService.listDisposableDomains(this.blocklistSourceFilter);
        this.blocklist = resp.data || [];
      } catch (e) {
        this.$log.error("loadBlocklist failed", e);
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.loadError"), "error");
      } finally {
        this.loadingBlocklist = false;
      }
    },
    async loadAllowlist() {
      this.loadingAllowlist = true;
      try {
        const resp = await UserApiService.listEmailAllowlist();
        this.allowlist = resp.data || [];
      } catch (e) {
        this.$log.error("loadAllowlist failed", e);
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.loadError"), "error");
      } finally {
        this.loadingAllowlist = false;
      }
    },
    openAddBlocklistDialog() {
      this.addDialog = {
        open: true,
        target: "blocklist",
        domain: "",
        note: "",
        valid: true,
        saving: false
      };
      this.$nextTick(() => this.$refs.addForm && this.$refs.addForm.resetValidation());
    },
    openAddAllowlistDialog() {
      this.addDialog = {
        open: true,
        target: "allowlist",
        domain: "",
        note: "",
        valid: true,
        saving: false
      };
      this.$nextTick(() => this.$refs.addForm && this.$refs.addForm.resetValidation());
    },
    closeAddDialog() {
      this.addDialog.open = false;
    },
    async submitAddDialog() {
      if (!this.$refs.addForm.validate()) return;
      this.addDialog.saving = true;
      const payload = {
        domain: this.addDialog.domain.trim(),
        note: this.addDialog.note ? this.addDialog.note.trim() : null
      };
      try {
        if (this.addDialog.target === "allowlist") {
          await UserApiService.addEmailAllowlistEntry(payload);
          await this.loadAllowlist();
        } else {
          await UserApiService.addDisposableDomain(payload);
          await this.loadBlocklist();
        }
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.addSuccess"));
        this.closeAddDialog();
      } catch (e) {
        this.$log.error("addDomain failed", e);
        const message =
          (e.response && e.response.headers && e.response.headers["x-ooblee-error-message"]) ||
          this.$i18n.t("pages.disposableEmails.saveError");
        this.showSnackbar(message, "error");
      } finally {
        this.addDialog.saving = false;
      }
    },
    async confirmDeleteBlocklist(item) {
      const isGithub = item.source === "GITHUB";
      const messageKey = isGithub
        ? "pages.disposableEmails.confirmDeleteGithubMessage"
        : "pages.disposableEmails.confirmDeleteMessage";
      const ok = await this.$refs.confirm.open(
        this.$i18n.t("pages.disposableEmails.confirmDeleteTitle"),
        this.$i18n.t(messageKey, { domain: item.domain })
      );
      if (!ok) return;
      try {
        await UserApiService.deleteDisposableDomain(item.domain);
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.deleteSuccess"));
        await this.loadBlocklist();
      } catch (e) {
        this.$log.error("deleteDisposableDomain failed", e);
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.deleteError"), "error");
      }
    },
    async confirmDeleteAllowlist(item) {
      const ok = await this.$refs.confirm.open(
        this.$i18n.t("pages.disposableEmails.confirmDeleteTitle"),
        this.$i18n.t("pages.disposableEmails.confirmDeleteMessage", { domain: item.domain })
      );
      if (!ok) return;
      try {
        await UserApiService.deleteEmailAllowlistEntry(item.domain);
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.deleteSuccess"));
        await this.loadAllowlist();
      } catch (e) {
        this.$log.error("deleteEmailAllowlistEntry failed", e);
        this.showSnackbar(this.$i18n.t("pages.disposableEmails.deleteError"), "error");
      }
    }
  }
};
</script>

<style scoped>
.v-data-table.v-data-table.v-data-table >>> td {
  font-size: 14px;
}
</style>
